/**
 * DICOM Viewer Component
 *
 * Professional medical image viewer using Cornerstone.js
 * Includes standard radiology tools: measurements, annotations, window presets
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import {
  RenderingEngine,
  Enums,
  init as csInit,
  eventTarget,
  EVENTS,
} from '@cornerstonejs/core'
import * as cornerstoneTools from '@cornerstonejs/tools'
import * as cornerstoneDICOMImageLoader from '@cornerstonejs/dicom-image-loader'
import { controlesPara, type ControlesDelVisor, type Ventana } from '@/domain/visor'
import { leerCaracteristicas } from './leer-caracteristicas'
import {
  ZoomIn,
  ZoomOut,
  Move,
  Contrast,
  RotateCcw,
  Maximize2,
  Loader2,
  AlertCircle,
  Ruler,
  MousePointer,
  Circle,
  Triangle,
  ArrowUpRight,
  FlipHorizontal,
  FlipVertical,
  RotateCw,
  SunMoon,
  Sliders,
  Star,
  Trash2,
  ChevronDown,
} from 'lucide-react'

import { Button } from '@/presentation/components/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { cn } from '@/lib/utils'

// =============================================================================
// Types
// =============================================================================

interface DicomViewerProps {
  /** Study ID to load */
  studyId: string
  /** Image URLs (wadouri or wadors format) */
  imageUrls?: string[]
  /** Optional callback when image loads */
  onImageLoad?: () => void
  /** Optional callback on error */
  onError?: (error: Error) => void
  /** Show toolbar */
  showToolbar?: boolean
  /** Initial tool */
  initialTool?: ToolName
  /** Callback when key image is marked */
  onKeyImage?: (imageIndex: number, imageId: string) => void
  /** Callback when current image index changes (for thumbnail sync) */
  onImageIndexChange?: (index: number) => void
  /** External control of current image index */
  currentImageIndex?: number
  /**
   * Si el usuario puede medir y anotar sobre la imagen.
   *
   * Medir y anotar es un acto reservado: `annotation:create` pertenece al
   * RADIOLOGIST y a nadie más. Hasta ahora el visor ofrecía esas herramientas
   * a cualquiera que lo abriera, incluidos tecnólogo, transcriptor y médico
   * remitente, que no tienen ese permiso; la interfaz contradecía al modelo
   * de seguridad.
   *
   * Una medición en milímetros depende del `pixel spacing` del DICOM y, en
   * radiografía de proyección, de la magnificación geométrica. Quien no sabe
   * eso lee un número que parece un dato clínico y no lo es.
   *
   * Por defecto NO se puede: quien deba medir tiene que declararlo, en vez de
   * quedar abierto para quien olvide cerrarlo.
   */
  puedeAnotar?: boolean
}

/** Herramientas que crean anotaciones o mediciones sobre la imagen. */
const HERRAMIENTAS_DE_ANOTACION: readonly ToolName[] = [
  'Length',
  'EllipticalROI',
  'RectangleROI',
  'Angle',
  'Bidirectional',
  'ArrowAnnotate',
  // La sonda tambien deja un punto sobre la imagen y lee unidades Hounsfield:
  // es lectura clinica, no navegacion.
  'Probe',
]

type ToolName =
  | 'WindowLevel'
  | 'Pan'
  | 'Zoom'
  | 'StackScroll'
  | 'Length'
  | 'Probe'
  | 'EllipticalROI'
  | 'Angle'
  | 'ArrowAnnotate'
  | 'Bidirectional'
  | 'RectangleROI'

interface ViewportInfo {
  windowCenter: number
  windowWidth: number
  zoom: number
  imageIndex: number
  totalImages: number
  rotation: number
  flipH: boolean
  flipV: boolean
  inverted: boolean
}

// =============================================================================
// Cornerstone Initialization
// =============================================================================

let isInitialized = false
let initPromise: Promise<void> | null = null

async function initCornerstone(): Promise<void> {
  if (isInitialized) return
  if (initPromise) return initPromise

  initPromise = (async () => {
    try {
      console.log('[DicomViewer] Initializing Cornerstone...')

      await csInit()
      console.log('[DicomViewer] Cornerstone core initialized')

      const numWorkers = Math.min(navigator.hardwareConcurrency || 1, 4)
      await cornerstoneDICOMImageLoader.init({ maxWebWorkers: numWorkers })
      console.log('[DicomViewer] DICOM image loader initialized')

      // Initialize codecs
      try {
        const initializers = cornerstoneDICOMImageLoader.initializers
        if (initializers) {
          await Promise.all([
            initializers.HTJ2K?.(),
            initializers.JPEG2000?.(),
            initializers.JPEGLS?.(),
            initializers.JPEGBaseline12Bit?.(),
            initializers.JPEGLossless?.(),
            initializers.JPEGBaseline8Bit?.(),
          ].filter(Boolean))
        }
      } catch (codecError) {
        console.warn('[DicomViewer] Some codecs failed:', codecError)
      }

      // Initialize tools
      await cornerstoneTools.init()

      // Add event listeners
      eventTarget.addEventListener(EVENTS.IMAGE_LOADED, (evt: any) => {
        console.log('[DicomViewer] IMAGE_LOADED:', evt.detail?.image?.imageId?.slice(0, 80))
      })
      eventTarget.addEventListener(EVENTS.IMAGE_LOAD_FAILED, (evt: any) => {
        console.error('[DicomViewer] IMAGE_LOAD_FAILED:', evt.detail)
      })

      // Register all tools
      cornerstoneTools.addTool(cornerstoneTools.WindowLevelTool)
      cornerstoneTools.addTool(cornerstoneTools.PanTool)
      cornerstoneTools.addTool(cornerstoneTools.ZoomTool)
      cornerstoneTools.addTool(cornerstoneTools.StackScrollTool)
      cornerstoneTools.addTool(cornerstoneTools.LengthTool)
      cornerstoneTools.addTool(cornerstoneTools.ProbeTool)
      cornerstoneTools.addTool(cornerstoneTools.EllipticalROITool)
      cornerstoneTools.addTool(cornerstoneTools.AngleTool)
      cornerstoneTools.addTool(cornerstoneTools.ArrowAnnotateTool)
      cornerstoneTools.addTool(cornerstoneTools.BidirectionalTool)
      cornerstoneTools.addTool(cornerstoneTools.RectangleROITool)

      isInitialized = true
      console.log('[DicomViewer] Initialization complete')
    } catch (error) {
      console.error('[DicomViewer] Initialization failed:', error)
      initPromise = null
      throw error
    }
  })()

  return initPromise
}

const direccionDelArchivo = (url: string): string => url.replace(/^wado(?:uri|rs):/, '')

const LARGO_DE_LA_FIRMA_DICOM = 132

const esCancelacion = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError'

async function comprobarQueLaCabeceraEsDicom(url: string, cancelacion: AbortSignal): Promise<void> {
  const respuesta = await fetch(url, { signal: cancelacion })
  if (!respuesta.ok) {
    throw new Error(`No se pudo leer la imagen del estudio (${respuesta.status})`)
  }

  const cabecera = await leerPrimerosBytes(respuesta, LARGO_DE_LA_FIRMA_DICOM)
  if (cabecera.length < LARGO_DE_LA_FIRMA_DICOM) {
    throw new Error('El archivo del estudio es demasiado corto para ser una imagen DICOM')
  }

  const firma = String.fromCharCode(cabecera[128], cabecera[129], cabecera[130], cabecera[131])
  if (firma !== 'DICM') {
    throw new Error('El archivo del estudio no es una imagen DICOM')
  }
}

async function leerPrimerosBytes(respuesta: Response, cuantos: number): Promise<Uint8Array> {
  if (!respuesta.body) {
    return new Uint8Array(await respuesta.arrayBuffer()).subarray(0, cuantos)
  }

  const lector = respuesta.body.getReader()
  const trozos: Uint8Array[] = []
  let leidos = 0

  try {
    while (leidos < cuantos) {
      const { done, value } = await lector.read()
      if (done) break
      trozos.push(value)
      leidos += value.length
    }
  } finally {
    await lector.cancel().catch(() => undefined)
  }

  const cabecera = new Uint8Array(leidos)
  let posicion = 0
  for (const trozo of trozos) {
    cabecera.set(trozo, posicion)
    posicion += trozo.length
  }
  return cabecera
}

// =============================================================================
// Tool Button Component
// =============================================================================

interface ToolButtonProps {
  icon: React.ReactNode
  label: string
  active?: boolean
  onClick: () => void
  className?: string
  /**
   * Pinta el rotulo junto al icono.
   *
   * POR QUE HIZO FALTA
   * ==================
   * Toda la barra eran iconos con el nombre en `title`, que solo aparece si
   * el raton se queda quieto encima, y en una pantalla tactil no aparece
   * nunca. Un radiologo experto abrio el visor el 22/09/2026 y dijo que no
   * habia herramienta para medir distancia. Estaba: no se veia.
   *
   * Lo que no se ve no existe, y en un visor clinico eso se paga caro: el
   * medico asume que el producto no lo tiene y deja de mirarlo.
   */
  mostrarTexto?: boolean
}

function ToolButton({
  icon,
  label,
  active,
  onClick,
  className,
  mostrarTexto,
}: ToolButtonProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "rounded-lg transition-all flex items-center gap-1.5",
        mostrarTexto ? "px-2.5 py-2" : "p-2",
        active
          ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
          : "bg-slate-800/80 text-slate-300 hover:bg-slate-700 hover:text-white",
        className
      )}
    >
      {icon}
      {mostrarTexto && <span className="text-xs font-medium whitespace-nowrap">{label}</span>}
    </button>
  )
}

// =============================================================================
// Dropdown Menu Component
// =============================================================================

interface DropdownProps {
  trigger: React.ReactNode
  children: React.ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
}

function Dropdown({ trigger, children, open, onOpenChange }: DropdownProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onOpenChange(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onOpenChange])

  return (
    <div ref={ref} className="relative">
      <div onClick={() => onOpenChange(!open)}>{trigger}</div>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 min-w-[160px] py-1">
          {children}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Component
// =============================================================================

export function DicomViewer({
  studyId,
  imageUrls = [],
  onImageLoad,
  onError,
  showToolbar = true,
  initialTool = 'WindowLevel',
  onKeyImage,
  onImageIndexChange,
  currentImageIndex,
  puedeAnotar = false,
}: DicomViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const renderingEngineRef = useRef<RenderingEngine | null>(null)
  const cancelarLecturaRef = useRef<AbortController | null>(null)
  const prelecturaHabilitadaRef = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTool, setActiveTool] = useState<ToolName>(initialTool)
  const [viewportInfo, setViewportInfo] = useState<ViewportInfo | null>(null)
  const [controles, setControles] = useState<ControlesDelVisor | null>(null)
  const [keyImages, setKeyImages] = useState<Set<number>>(new Set())
  const [showPresets, setShowPresets] = useState(false)
  const [showMeasureMenu, setShowMeasureMenu] = useState(false)

  // Annotation modal state
  const [annotationModal, setAnnotationModal] = useState<{
    open: boolean
    text: string
    annotationUID: string | null
  }>({ open: false, text: '', annotationUID: null })

  const viewportId = `viewport-${studyId}`
  const renderingEngineId = `engine-${studyId}`
  const toolGroupId = `toolGroup-${studyId}`

  // Initialize and setup viewport
  useEffect(() => {
    let mounted = true
    let engine: RenderingEngine | null = null

    let observadorDeTamano: ResizeObserver | null = null

    async function setup() {
      try {
        if (!containerRef.current) return

        setIsLoading(true)
        setError(null)

        await initCornerstone()
        if (!mounted) return

        engine = new RenderingEngine(renderingEngineId)
        renderingEngineRef.current = engine

        const viewportInput = {
          viewportId,
          type: Enums.ViewportType.STACK,
          element: containerRef.current,
          defaultOptions: {
            background: [0, 0, 0] as [number, number, number],
          },
        }

        engine.enableElement(viewportInput)

        /* REENCAJAR CUANDO CAMBIA EL TAMANO
           =================================
           Cornerstone dimensiona su lienzo al habilitar el elemento y no se
           entera de los cambios posteriores. En movil el contenedor cambia
           varias veces: al asentarse la maqueta, al conmutar Imagen/Informe
           (que lo oculta y lo vuelve a mostrar) y al girar el telefono. Sin
           esto el lienzo conserva la medida vieja y la imagen queda cortada.

           `resize(true, false)` reencaja en vez de conservar la camara: un
           cambio de tamano real es justo cuando el encuadre anterior ya no
           vale. Se ignora el tamano cero, que es el contenedor oculto. */
        let reencajePendiente = 0
        observadorDeTamano = new ResizeObserver((entradas) => {
          const caja = entradas[0]?.contentRect
          if (!caja || caja.width < 1 || caja.height < 1) return
          cancelAnimationFrame(reencajePendiente)
          reencajePendiente = requestAnimationFrame(() => {
            try {
              renderingEngineRef.current?.resize(true, false)
            } catch {
              // el motor pudo destruirse mientras esperaba el cuadro
            }
          })
        })
        observadorDeTamano.observe(containerRef.current)

        // Create tool group with all tools
        const toolGroup = cornerstoneTools.ToolGroupManager.createToolGroup(toolGroupId)
        if (toolGroup) {
          toolGroup.addViewport(viewportId, renderingEngineId)

          // Add all tools
          const tools = [
            cornerstoneTools.WindowLevelTool.toolName,
            cornerstoneTools.PanTool.toolName,
            cornerstoneTools.ZoomTool.toolName,
            cornerstoneTools.StackScrollTool.toolName,
            cornerstoneTools.LengthTool.toolName,
            cornerstoneTools.ProbeTool.toolName,
            cornerstoneTools.EllipticalROITool.toolName,
            cornerstoneTools.AngleTool.toolName,
            cornerstoneTools.ArrowAnnotateTool.toolName,
            cornerstoneTools.BidirectionalTool.toolName,
            cornerstoneTools.RectangleROITool.toolName,
          ]

          tools.forEach(tool => toolGroup.addTool(tool))

          // Set initial active tool
          toolGroup.setToolActive(cornerstoneTools.WindowLevelTool.toolName, {
            bindings: [{ mouseButton: cornerstoneTools.Enums.MouseBindings.Primary }],
          })

          // Enable stack scroll with mouse wheel
          toolGroup.setToolActive(cornerstoneTools.StackScrollTool.toolName, {
            bindings: [{ mouseButton: cornerstoneTools.Enums.MouseBindings.Wheel }],
          })

          // Configure ArrowAnnotate to use custom text callback
          toolGroup.setToolConfiguration(cornerstoneTools.ArrowAnnotateTool.toolName, {
            getTextCallback: (callback: (text: string) => void) => {
              // Store callback in window to access from React state
              (window as any).__arrowAnnotateCallback = callback
              // Dispatch custom event to trigger React modal
              window.dispatchEvent(new CustomEvent('openAnnotationModal'))
            },
            changeTextCallback: (data: any, _eventData: any, callback: (text: string) => void) => {
              (window as any).__arrowAnnotateCallback = callback
              ;(window as any).__arrowAnnotateCurrentText = data.text || ''
              window.dispatchEvent(new CustomEvent('openAnnotationModal', {
                detail: { currentText: data.text || '' }
              }))
            },
          })
        }

        if (imageUrls.length > 0) {
          await loadImages(imageUrls)
        }

        setIsLoading(false)
        onImageLoad?.()
      } catch (err) {
        console.error('[DicomViewer] Error:', err)
        const errorMessage = err instanceof Error ? err.message : 'Error desconocido'
        setError(errorMessage)
        setIsLoading(false)
        onError?.(err instanceof Error ? err : new Error(errorMessage))
      }
    }

    setup()

    return () => {
      mounted = false
      cancelarLecturaRef.current?.abort()
      cancelarLecturaRef.current = null
      // Primero el observador: si se destruye el motor antes, un reencaje
      // pendiente encontraria el lienzo ya desmontado.
      observadorDeTamano?.disconnect()
      observadorDeTamano = null
      if (containerRef.current && prelecturaHabilitadaRef.current) {
        cornerstoneTools.utilities.stackContextPrefetch.disable(containerRef.current)
        prelecturaHabilitadaRef.current = false
      }
      if (engine) engine.destroy()
      cornerstoneTools.ToolGroupManager.destroyToolGroup(toolGroupId)
    }
  }, [studyId])

  // Listen for annotation modal events
  useEffect(() => {
    const handleOpenModal = (e: Event) => {
      const customEvent = e as CustomEvent
      const currentText = customEvent.detail?.currentText || (window as any).__arrowAnnotateCurrentText || ''
      setAnnotationModal({ open: true, text: currentText, annotationUID: null })
    }

    window.addEventListener('openAnnotationModal', handleOpenModal)
    return () => window.removeEventListener('openAnnotationModal', handleOpenModal)
  }, [])

  // Handle annotation modal submit
  const handleAnnotationSubmit = useCallback(() => {
    const callback = (window as any).__arrowAnnotateCallback
    if (callback) {
      callback(annotationModal.text)
      delete (window as any).__arrowAnnotateCallback
      delete (window as any).__arrowAnnotateCurrentText
    }
    setAnnotationModal({ open: false, text: '', annotationUID: null })

    // Re-render viewport to show annotation
    const engine = renderingEngineRef.current
    if (engine) {
      const viewport = engine.getViewport(viewportId)
      viewport?.render()
    }
  }, [annotationModal.text, viewportId])

  // Handle annotation modal cancel
  const handleAnnotationCancel = useCallback(() => {
    const callback = (window as any).__arrowAnnotateCallback
    if (callback) {
      callback('') // Empty text cancels the annotation
      delete (window as any).__arrowAnnotateCallback
      delete (window as any).__arrowAnnotateCurrentText
    }
    setAnnotationModal({ open: false, text: '', annotationUID: null })
  }, [])

  const loadImages = useCallback(async (urls: string[]) => {
    try {
      const engine = renderingEngineRef.current
      if (!engine) return

      const viewport = engine.getViewport(viewportId)
      if (!viewport || viewport.type !== Enums.ViewportType.STACK) return

      const direcciones = urls.map(direccionDelArchivo)
      if (direcciones.length === 0) {
        throw new Error('El estudio no tiene imágenes que mostrar')
      }

      cancelarLecturaRef.current?.abort()
      const cancelacion = new AbortController()
      cancelarLecturaRef.current = cancelacion

      await comprobarQueLaCabeceraEsDicom(direcciones[0], cancelacion.signal)

      const identificadores = direcciones.map((url) => `wadouri:${url}`)
      await (viewport as any).setStack(identificadores)

      setControles(controlesPara(leerCaracteristicas(identificadores[0], identificadores.length)))

      if (!prelecturaHabilitadaRef.current) {
        cornerstoneTools.utilities.stackContextPrefetch.enable(viewport.element)
        prelecturaHabilitadaRef.current = true
      }

      // Encajar la imagen en el lienzo.
      // `setStack` no ajusta la camara al tamano real del contenedor: deja la
      // escala con la que se habilito el elemento. En un telefono el contenedor
      // todavia no tiene su medida final en ese instante, asi que la ecografia
      // se pintaba a escala nativa y se salia por los lados. `resetCamera`
      // encaja; el `requestAnimationFrame` espera a que la maqueta asiente.
      viewport.resetCamera()
      viewport.render()
      requestAnimationFrame(() => {
        try {
          viewport.resetCamera()
          viewport.render()
        } catch {
          // el visor pudo desmontarse entre medias; no hay nada que encajar
        }
      })
    } catch (err) {
      if (esCancelacion(err)) return
      console.error('[DicomViewer] Error loading images:', err)
      throw err
    }
  }, [viewportId])

  // Update viewport info
  const updateViewportInfo = useCallback(() => {
    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId)
    if (!viewport) return

    const camera = viewport.getCamera()
    const properties = (viewport as any).getProperties?.() || {}

    setViewportInfo({
      windowCenter: Math.round(properties.voiRange?.lower + (properties.voiRange?.upper - properties.voiRange?.lower) / 2) || 0,
      windowWidth: Math.round((properties.voiRange?.upper - properties.voiRange?.lower)) || 0,
      zoom: camera.parallelScale ? Math.round(1 / camera.parallelScale * 100) : 100,
      imageIndex: (viewport as any).getCurrentImageIdIndex?.() + 1 || 1,
      totalImages: (viewport as any).getImageIds?.()?.length || 1,
      rotation: (viewport as any).getRotation?.() || 0,
      flipH: (viewport as any).getFlipHorizontal?.() || false,
      flipV: (viewport as any).getFlipVertical?.() || false,
      inverted: properties.invert || false,
    })
  }, [viewportId])

  // Reload images when imageUrls changes (after initial setup)
  const prevImageUrlsRef = useRef<string[]>([])
  const hasInitializedRef = useRef(false)
  useEffect(() => {
    // Skip if no URLs or currently loading or engine not ready
    if (imageUrls.length === 0 || isLoading || !renderingEngineRef.current) return

    // Check if URLs actually changed (not just array reference)
    const urlsChanged = imageUrls.length !== prevImageUrlsRef.current.length ||
      imageUrls.some((url, i) => url !== prevImageUrlsRef.current[i])

    // Load images if URLs changed and we have more than before (new images added)
    // OR if URLs changed after initialization
    const shouldLoad = urlsChanged && (imageUrls.length > prevImageUrlsRef.current.length || hasInitializedRef.current)

    if (shouldLoad) {
      console.log('[DicomViewer] imageUrls changed, reloading images...')
      setIsLoading(true)
      setError(null) // Clear any previous error
      loadImages(imageUrls)
        .then(() => {
          setIsLoading(false)
          setError(null) // Clear error on successful load
          updateViewportInfo()
        })
        .catch((err) => {
          console.error('[DicomViewer] Error reloading images:', err)
          setError(err instanceof Error ? err.message : 'Error loading images')
          setIsLoading(false)
        })
    }

    prevImageUrlsRef.current = imageUrls
    hasInitializedRef.current = true
  }, [imageUrls, isLoading, loadImages, updateViewportInfo])

  // Handle external image index changes (from thumbnail panel)
  useEffect(() => {
    if (currentImageIndex === undefined) return
    // Wait for loading to complete before trying to change image
    if (isLoading) return
    // Don't try to change images if there are no URLs to load
    if (!imageUrls || imageUrls.length === 0) return

    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId) as any
    if (!viewport) return

    // Check if viewport has images loaded
    const imageIds = viewport.getImageIds?.() ?? []
    if (imageIds.length === 0) {
      // Images might still be loading into viewport, silently return
      return
    }

    // Validate index is within bounds
    if (currentImageIndex < 0 || currentImageIndex >= imageIds.length) {
      console.warn(`[DicomViewer] Invalid image index: ${currentImageIndex}, total: ${imageIds.length}`)
      return
    }

    const currentIdx = viewport.getCurrentImageIdIndex?.() ?? 0
    if (currentIdx !== currentImageIndex) {
      console.log(`[DicomViewer] Changing image from ${currentIdx} to ${currentImageIndex}`)
      viewport.setImageIdIndex(currentImageIndex)
      viewport.render()
      updateViewportInfo()
    }
  }, [currentImageIndex, viewportId, updateViewportInfo, isLoading, imageUrls])

  // Notify parent when image index changes (from scroll wheel)
  useEffect(() => {
    if (!onImageIndexChange) return

    const element = containerRef.current
    if (!element) return

    const handleStackScroll = () => {
      const engine = renderingEngineRef.current
      if (!engine) return

      const viewport = engine.getViewport(viewportId) as any
      if (!viewport) return

      const newIndex = viewport.getCurrentImageIdIndex?.() ?? 0
      onImageIndexChange(newIndex)
      updateViewportInfo()
    }

    // Listen for wheel events on the container
    element.addEventListener('wheel', handleStackScroll)
    return () => element.removeEventListener('wheel', handleStackScroll)
  }, [onImageIndexChange, viewportId, updateViewportInfo])

  // Change active tool
  const changeTool = useCallback((toolName: ToolName) => {
    // No basta con no pintar el boton: el visor conserva la herramienta
    // activa entre imagenes y `initialTool` llega desde fuera. El cierre va
    // aqui, donde la herramienta se activa de verdad.
    if (!puedeAnotar && HERRAMIENTAS_DE_ANOTACION.includes(toolName)) return

    const toolGroup = cornerstoneTools.ToolGroupManager.getToolGroup(toolGroupId)
    if (!toolGroup) return

    // Map tool names
    const toolMap: Record<ToolName, string> = {
      WindowLevel: cornerstoneTools.WindowLevelTool.toolName,
      Pan: cornerstoneTools.PanTool.toolName,
      Zoom: cornerstoneTools.ZoomTool.toolName,
      StackScroll: cornerstoneTools.StackScrollTool.toolName,
      Length: cornerstoneTools.LengthTool.toolName,
      Probe: cornerstoneTools.ProbeTool.toolName,
      EllipticalROI: cornerstoneTools.EllipticalROITool.toolName,
      Angle: cornerstoneTools.AngleTool.toolName,
      ArrowAnnotate: cornerstoneTools.ArrowAnnotateTool.toolName,
      Bidirectional: cornerstoneTools.BidirectionalTool.toolName,
      RectangleROI: cornerstoneTools.RectangleROITool.toolName,
    }

    // Deactivate all tools
    Object.values(toolMap).forEach(name => {
      try {
        toolGroup.setToolPassive(name)
      } catch {
        // Tool might not exist
      }
    })

    // Activate selected tool
    toolGroup.setToolActive(toolMap[toolName], {
      bindings: [{ mouseButton: cornerstoneTools.Enums.MouseBindings.Primary }],
    })

    setActiveTool(toolName)
    setShowMeasureMenu(false)
  }, [toolGroupId, puedeAnotar])

  const aplicarVentana = useCallback((ventana: Ventana) => {
    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId)
    if (!viewport) return

    const lower = ventana.centro - ventana.ancho / 2
    const upper = ventana.centro + ventana.ancho / 2

    ;(viewport as any).setProperties?.({
      voiRange: { lower, upper },
    })
    viewport.render()
    updateViewportInfo()
    setShowPresets(false)
  }, [viewportId, updateViewportInfo])

  // Invert image
  const toggleInvert = useCallback(() => {
    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId)
    if (!viewport) return

    const properties = (viewport as any).getProperties?.() || {}
    ;(viewport as any).setProperties?.({
      invert: !properties.invert,
    })
    viewport.render()
    updateViewportInfo()
  }, [viewportId, updateViewportInfo])

  // Rotate image
  const rotateImage = useCallback((degrees: number) => {
    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId)
    if (!viewport) return

    const currentRotation = (viewport as any).getRotation?.() || 0
    ;(viewport as any).setRotation?.(currentRotation + degrees)
    viewport.render()
    updateViewportInfo()
  }, [viewportId, updateViewportInfo])

  // Flip image
  const flipImage = useCallback((direction: 'horizontal' | 'vertical') => {
    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId)
    if (!viewport) return

    if (direction === 'horizontal') {
      const current = (viewport as any).getFlipHorizontal?.() || false
      ;(viewport as any).setFlipHorizontal?.(!current)
    } else {
      const current = (viewport as any).getFlipVertical?.() || false
      ;(viewport as any).setFlipVertical?.(!current)
    }
    viewport.render()
    updateViewportInfo()
  }, [viewportId, updateViewportInfo])

  // Reset viewport
  const resetViewport = useCallback(() => {
    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId)
    if (!viewport) return

    viewport.resetCamera()
    ;(viewport as any).setRotation?.(0)
    ;(viewport as any).setFlipHorizontal?.(false)
    ;(viewport as any).setFlipVertical?.(false)
    ;(viewport as any).setProperties?.({ invert: false })
    viewport.render()
    updateViewportInfo()
  }, [viewportId, updateViewportInfo])

  // Zoom controls
  const handleZoom = useCallback((direction: 'in' | 'out') => {
    const engine = renderingEngineRef.current
    if (!engine) return

    const viewport = engine.getViewport(viewportId)
    if (!viewport) return

    const camera = viewport.getCamera()
    const zoomFactor = direction === 'in' ? 0.8 : 1.2

    viewport.setCamera({
      ...camera,
      parallelScale: (camera.parallelScale || 1) * zoomFactor,
    })
    viewport.render()
    updateViewportInfo()
  }, [viewportId, updateViewportInfo])

  // Toggle key image
  const toggleKeyImage = useCallback(() => {
    if (!viewportInfo) return

    const imageIndex = viewportInfo.imageIndex - 1
    const newKeyImages = new Set(keyImages)

    if (newKeyImages.has(imageIndex)) {
      newKeyImages.delete(imageIndex)
    } else {
      newKeyImages.add(imageIndex)
      onKeyImage?.(imageIndex, `image-${imageIndex}`)
    }

    setKeyImages(newKeyImages)
  }, [viewportInfo, keyImages, onKeyImage])

  // Clear all annotations
  const clearAnnotations = useCallback(() => {
    const annotationState = cornerstoneTools.annotation.state
    annotationState.removeAllAnnotations()

    const engine = renderingEngineRef.current
    if (engine) {
      const viewport = engine.getViewport(viewportId)
      viewport?.render()
    }
  }, [viewportId])

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return

    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      containerRef.current.requestFullscreen()
    }
  }, [])

  const isKeyImage = viewportInfo ? keyImages.has(viewportInfo.imageIndex - 1) : false

  return (
    <div className="flex flex-col h-full w-full bg-black">
      {/* Main Toolbar - Fixed header area, not overlapping image */}
      {showToolbar && !isLoading && !error && (
        <div className="relative z-50 flex-shrink-0 bg-slate-900/95 backdrop-blur border-b border-slate-700/50 px-3 py-2">
          {/* En telefono NO se parte en varias filas: se deja UNA fila que
              se desplaza en horizontal dentro de su propia caja. Con
              `flex-wrap` la barra ocupaba dos filas enteras de botones
              grandes, un cuarto de la pantalla, y dejaba la imagen en una
              franja. El contenido ancho se desplaza en su contenedor; la
              pagina, nunca. */}
          <div className="flex flex-nowrap lg:flex-wrap gap-1.5 overflow-x-auto lg:overflow-x-visible">
          {/* Basic Tools */}
          <div className="flex gap-1 bg-slate-900/90 backdrop-blur p-1.5 rounded-lg border border-slate-700/50">
            <ToolButton
              icon={<Contrast className="h-4 w-4" />}
              label="Ventana/Nivel"
              active={activeTool === 'WindowLevel'}
              onClick={() => changeTool('WindowLevel')}
            />
            <ToolButton
              icon={<Move className="h-4 w-4" />}
              label="Mover"
              active={activeTool === 'Pan'}
              onClick={() => changeTool('Pan')}
            />
            <ToolButton
              icon={<ZoomIn className="h-4 w-4" />}
              label="Zoom"
              active={activeTool === 'Zoom'}
              onClick={() => changeTool('Zoom')}
            />
          </div>

          {/* Medición y anotación: solo para quien tiene annotation:create */}
          {puedeAnotar && (
          <div className="flex gap-1 bg-slate-900/90 backdrop-blur p-1.5 rounded-lg border border-slate-700/50">
            {/* Medir una distancia es, con diferencia, lo que mas se hace.
                Tiene boton propio y con su nombre escrito: escondido dentro
                de un desplegable con icono de regla, un radiologo experto no
                lo encontro. */}
            <ToolButton
              icon={<Ruler className="h-4 w-4" />}
              label="Medir"
              mostrarTexto
              active={activeTool === 'Length'}
              onClick={() => changeTool('Length')}
            />

            <Dropdown
              trigger={
                <button
                  aria-label="Más medidas"
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-all",
                    ['EllipticalROI', 'Angle', 'Bidirectional', 'RectangleROI'].includes(activeTool)
                      ? "bg-blue-500 text-white"
                      : "bg-slate-800/80 text-slate-300 hover:bg-slate-700"
                  )}
                >
                  <Circle className="h-4 w-4" />
                  <span className="text-xs font-medium whitespace-nowrap">Más medidas</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              }
              open={showMeasureMenu}
              onOpenChange={setShowMeasureMenu}
            >
              <button
                onClick={() => changeTool('EllipticalROI')}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-700",
                  activeTool === 'EllipticalROI' && "bg-blue-500/20 text-blue-400"
                )}
              >
                <Circle className="h-4 w-4" />
                Área (Elipse)
              </button>
              <button
                onClick={() => changeTool('RectangleROI')}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-700",
                  activeTool === 'RectangleROI' && "bg-blue-500/20 text-blue-400"
                )}
              >
                <div aria-hidden="true" className="h-4 w-4 border border-current" />
                Área (Rectángulo)
              </button>
              <button
                onClick={() => changeTool('Angle')}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-700",
                  activeTool === 'Angle' && "bg-blue-500/20 text-blue-400"
                )}
              >
                <Triangle className="h-4 w-4" />
                Ángulo
              </button>
              <button
                onClick={() => changeTool('Bidirectional')}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-700",
                  activeTool === 'Bidirectional' && "bg-blue-500/20 text-blue-400"
                )}
              >
                <span aria-hidden="true" className="h-4 w-4 flex items-center justify-center text-xs">⊕</span>
                Bidireccional
              </button>
            </Dropdown>

            <ToolButton
              icon={<ArrowUpRight className="h-4 w-4" />}
              label="Flecha"
              mostrarTexto
              active={activeTool === 'ArrowAnnotate'}
              onClick={() => changeTool('ArrowAnnotate')}
            />
            <ToolButton
              icon={<MousePointer className="h-4 w-4" />}
              label="Sonda (HU)"
              mostrarTexto
              active={activeTool === 'Probe'}
              onClick={() => changeTool('Probe')}
            />
          </div>
          )}

          {(controles?.ventanas.length ?? 0) > 0 && (
          <div className="flex gap-1 bg-slate-900/90 backdrop-blur p-1.5 rounded-lg border border-slate-700/50">
            <Dropdown
              trigger={
                <button className="flex items-center gap-1 px-2 py-2 rounded-lg bg-slate-800/80 text-slate-300 hover:bg-slate-700 transition-all">
                  <Sliders className="h-4 w-4" />
                  <ChevronDown className="h-3 w-3" />
                </button>
              }
              open={showPresets}
              onOpenChange={setShowPresets}
            >
              <div className="px-2 py-1 text-xs text-slate-500 uppercase tracking-wider">
                Ventanas
              </div>
              {controles?.ventanas.map((ventana) => (
                <button
                  key={ventana.nombre}
                  onClick={() => aplicarVentana(ventana)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-slate-700 text-slate-300"
                >
                  <span>{ventana.nombre}</span>
                  <span className="text-xs text-slate-500">
                    W:{Math.round(ventana.ancho)} L:{Math.round(ventana.centro)}
                  </span>
                </button>
              ))}
            </Dropdown>
          </div>
          )}

          {/* Visualization Tools */}
          <div className="flex gap-1 bg-slate-900/90 backdrop-blur p-1.5 rounded-lg border border-slate-700/50">
            <ToolButton
              icon={<SunMoon className="h-4 w-4" />}
              label="Invertir"
              active={viewportInfo?.inverted}
              onClick={toggleInvert}
            />
            <ToolButton
              icon={<RotateCw className="h-4 w-4" />}
              label="Rotar 90°"
              onClick={() => rotateImage(90)}
            />
            <ToolButton
              icon={<FlipHorizontal className="h-4 w-4" />}
              label="Voltear H"
              active={viewportInfo?.flipH}
              onClick={() => flipImage('horizontal')}
            />
            <ToolButton
              icon={<FlipVertical className="h-4 w-4" />}
              label="Voltear V"
              active={viewportInfo?.flipV}
              onClick={() => flipImage('vertical')}
            />
          </div>

          {/* Zoom & Actions */}
          <div className="flex gap-1 bg-slate-900/90 backdrop-blur p-1.5 rounded-lg border border-slate-700/50">
            <ToolButton
              icon={<ZoomIn className="h-4 w-4" />}
              label="Acercar"
              onClick={() => handleZoom('in')}
            />
            <ToolButton
              icon={<ZoomOut className="h-4 w-4" />}
              label="Alejar"
              onClick={() => handleZoom('out')}
            />
            <ToolButton
              icon={<RotateCcw className="h-4 w-4" />}
              label="Restablecer"
              onClick={resetViewport}
            />
            <ToolButton
              icon={<Maximize2 className="h-4 w-4" />}
              label="Pantalla completa"
              onClick={toggleFullscreen}
            />
          </div>

          {/* Imagen clave y limpiar. Marcar una imagen como clave es parte
              del trabajo de quien informa: se ofrece solo si la pantalla que
              usa el visor recoge esa marca (onKeyImage). En el portal del
              paciente nadie la recoge, y un boton que no hace nada es peor
              que no tenerlo. */}
          {(onKeyImage || puedeAnotar) && (
          <div className="flex gap-1 bg-slate-900/90 backdrop-blur p-1.5 rounded-lg border border-slate-700/50">
            {onKeyImage && (
              <ToolButton
                icon={<Star className={cn("h-4 w-4", isKeyImage && "fill-yellow-400")} />}
                label="Imagen clave"
                active={isKeyImage}
                onClick={toggleKeyImage}
                className={isKeyImage ? "bg-yellow-500 text-black" : undefined}
              />
            )}
            {puedeAnotar && (
              <ToolButton
                icon={<Trash2 className="h-4 w-4" />}
                label="Limpiar anotaciones"
                onClick={clearAnnotations}
              />
            )}
          </div>
          )}
          </div>
        </div>
      )}

      {/* Viewport Area */}
      <div className="relative flex-1 min-h-0">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white z-20">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Cargando el estudio…</p>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white z-20">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Viewport Info Overlay */}
      {viewportInfo && !isLoading && !error && (
        <div className="absolute bottom-3 left-3 z-10 bg-slate-900/90 backdrop-blur px-3 py-2 rounded-lg border border-slate-700/50 text-white text-xs font-mono space-y-0.5">
          <div className="flex items-center gap-4">
            <span>W: {viewportInfo.windowWidth}</span>
            <span>L: {viewportInfo.windowCenter}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>Zoom: {viewportInfo.zoom}%</span>
            {viewportInfo.rotation !== 0 && <span>Rot: {viewportInfo.rotation}°</span>}
          </div>
          <div className="flex items-center gap-2">
            <span>Img: {viewportInfo.imageIndex}/{viewportInfo.totalImages}</span>
            {isKeyImage && <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />}
          </div>
          {controles && !controles.medidaEnMilimetros && (
            <div className="text-amber-400/90">Sin calibración: se mide en píxeles</div>
          )}
        </div>
      )}

      {/* Key Images indicator */}
      {keyImages.size > 0 && (
        <div className="absolute bottom-3 right-3 z-10 bg-yellow-500/20 backdrop-blur px-3 py-2 rounded-lg border border-yellow-500/50 text-yellow-400 text-xs">
          <div className="flex items-center gap-2">
            <Star className="h-3 w-3 fill-current" />
            {keyImages.size} imagen{keyImages.size > 1 ? 'es' : ''} clave
          </div>
        </div>
      )}

      {/* Viewport Container */}
      <div
        ref={containerRef}
        className="h-full w-full"
        onContextMenu={(e) => e.preventDefault()}
      />

      {/* No images message */}
      {imageUrls.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center text-white/50">
          <p>No hay imágenes disponibles para este estudio</p>
        </div>
      )}
      </div>

      {/* Annotation Text Modal */}
      <Dialog open={annotationModal.open} onOpenChange={(open) => !open && handleAnnotationCancel()}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Agregar Anotación</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-400 mb-2">
              Texto de la anotación
            </label>
            <input
              type="text"
              value={annotationModal.text}
              onChange={(e) => setAnnotationModal(prev => ({ ...prev, text: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAnnotationSubmit()
                if (e.key === 'Escape') handleAnnotationCancel()
              }}
              placeholder="Ingrese el texto de la anotación..."
              autoFocus
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={handleAnnotationCancel}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAnnotationSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
